import { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Input, Modal, Space, Flex, Tooltip, theme } from 'antd';
import { CalculatorOutlined, CheckOutlined } from '@ant-design/icons';
import { evaluate } from 'mathjs';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    const rounded = Math.round(num * 1e10) / 1e10;
    const str = String(rounded);
    const [intPart, decPart] = str.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart ? `${formattedInt}.${decPart}` : formattedInt;
};

const parseRawNumber = (str) => {
    const cleaned = str.replace(/,/g, '').trim();
    const num = Number(cleaned);
    return isNaN(num) ? null : num;
};

const isExpression = (str) => {
    const cleaned = str.replace(/,/g, '').trim();
    if (/^-?\d*\.?\d+$/.test(cleaned)) return false;
    return /[+\-*\/x:％%]/.test(cleaned);
};

const preprocessExpression = (str) => {
    str = str.replace(/,/g, '');
    str = str.replace(/x/gi, '*');
    str = str.replace(/:/g, '/');
    str = str.replace(/％/g, '%');
    // "A + B%" / "A - B%"  →  "A + (A * B / 100)"
    str = str.replace(
        /(-?\d+\.?\d*)\s*([+\-])\s*(\d+\.?\d*)\s*%/g,
        (_, a, op, b) => `${a} ${op} (${a} * ${b} / 100)`,
    );
    // standalone "B%" → "B/100"
    str = str.replace(/(\d+\.?\d*)\s*%/g, (_, b) => `${b}/100`);
    return str;
};

const evaluateExpression = (str) => {
    try {
        const expr = preprocessExpression(str);
        const result = evaluate(expr);
        if (typeof result !== 'number' || !isFinite(result)) return null;
        return result;
    } catch {
        return null;
    }
};

// ─── Calculator Modal ────────────────────────────────────────────────────────

const KEYPAD = [
    [
        { label: 'C',    action: 'clear',    type: 'danger'   },
        { label: '±',    action: 'negate',   type: 'default'  },
        { label: '%',    action: 'append',   value: '%',  type: 'default' },
        { label: '÷',    action: 'append',   value: '/',  type: 'op'     },
    ],
    [
        { label: '7',    action: 'append',   value: '7'   },
        { label: '8',    action: 'append',   value: '8'   },
        { label: '9',    action: 'append',   value: '9'   },
        { label: '×',    action: 'append',   value: '*',  type: 'op'     },
    ],
    [
        { label: '4',    action: 'append',   value: '4'   },
        { label: '5',    action: 'append',   value: '5'   },
        { label: '6',    action: 'append',   value: '6'   },
        { label: '−',    action: 'append',   value: '-',  type: 'op'     },
    ],
    [
        { label: '1',    action: 'append',   value: '1'   },
        { label: '2',    action: 'append',   value: '2'   },
        { label: '3',    action: 'append',   value: '3'   },
        { label: '+',    action: 'append',   value: '+',  type: 'op'     },
    ],
    [
        { label: '+1K',  action: 'thousand', value: 1000,  type: 'k' },
        { label: '0',    action: 'append',   value: '0'   },
        { label: '.',    action: 'append',   value: '.'   },
        { label: '=',    action: 'eval',     type: 'primary' },
    ],
    [
        { label: '−1K',  action: 'thousand', value: -1000,  type: 'k' },
        { label: '+10K', action: 'thousand', value: 10000,  type: 'k' },
        { label: '−10K', action: 'thousand', value: -10000, type: 'k' },
        { label: '⌫',    action: 'backspace', type: 'default' },
    ],
    [
        { label: '×1K',  action: 'x1000',   type: 'k'      },
        { label: '÷1K',  action: 'div1000', type: 'k'      },
        { label: '⌊↓⌋', action: 'floor',   type: 'round'  },
        { label: '⌈↑⌉', action: 'ceil',    type: 'round'  },
    ],
];

const CalculatorModal = ({ open, onClose, onApply, token, initialValue }) => {
    const [expr, setExpr] = useState('');
    const [result, setResult] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [justEvaled, setJustEvaled] = useState(false);
    const inputRef = useRef();

    useEffect(() => {
        if (open) {
            const initialExpr = initialValue != null ? String(initialValue) : '';
            setExpr(initialExpr);
            setResult(initialExpr ? evaluateExpression(initialExpr) : null);
            setHasError(false);
            setJustEvaled(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open, initialValue]);

    const recalc = (newExpr) => {
        if (newExpr.trim() === '') { setResult(null); setHasError(false); return; }
        const res = evaluateExpression(newExpr);
        if (res !== null) { setResult(res); setHasError(false); }
        else { setResult(null); setHasError(true); }
    };

    const push = (newExpr) => {
        setExpr(newExpr);
        recalc(newExpr);
    };

    const handleKey = (key) => {
        if (key.action === 'clear') {
            setExpr(''); setResult(null); setHasError(false); setJustEvaled(false);
        } else if (key.action === 'backspace') {
            const next = justEvaled ? '' : expr.slice(0, -1);
            setJustEvaled(false); push(next);
        } else if (key.action === 'negate') {
            const base = result !== null ? String(result) : expr;
            const next = base.startsWith('-') ? base.slice(1) : `-${base}`;
            setJustEvaled(false); push(next);
        } else if (key.action === 'eval') {
            if (result !== null) {
                const res = String(Math.round(result * 1e10) / 1e10);
                setExpr(res); setJustEvaled(true); recalc(res);
            }
        } else if (key.action === 'thousand') {
            const base = result !== null ? result : (parseRawNumber(expr) ?? 0);
            const next = String(Math.round((base + key.value) * 1e10) / 1e10);
            setJustEvaled(false); push(next);
        } else if (key.action === 'x1000') {
            const base = result !== null ? result : (parseRawNumber(expr) ?? 0);
            const next = String(Math.round(base * 1000 * 1e10) / 1e10);
            setJustEvaled(false); push(next);
        } else if (key.action === 'div1000') {
            const base = result !== null ? result : (parseRawNumber(expr) ?? 0);
            const next = String(Math.round((base / 1000) * 1e10) / 1e10);
            setJustEvaled(false); push(next);
        } else if (key.action === 'floor') {
            const base = result !== null ? result : (parseRawNumber(expr) ?? 0);
            const next = String(Math.floor(base));
            setJustEvaled(false); push(next);
        } else if (key.action === 'ceil') {
            const base = result !== null ? result : (parseRawNumber(expr) ?? 0);
            const next = String(Math.ceil(base));
            setJustEvaled(false); push(next);
        } else if (key.action === 'append') {
            const start = justEvaled && /\d/.test(key.value) ? '' : expr;
            setJustEvaled(false); push(start + key.value);
        }
    };

    const handleInputChange = (e) => { setJustEvaled(false); push(e.target.value); };

    const handleApply = () => {
        if (result !== null) { onApply(result); onClose(); }
    };

    const cellStyle = (key) => {
        const base = {
            flex: 1, height: 44, fontSize: 15, fontWeight: 500,
            borderRadius: token.borderRadius, cursor: 'pointer',
            border: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            userSelect: 'none', transition: 'opacity 0.1s',
        };
        if (key.type === 'primary')  return { ...base, background: token.colorPrimary,  color: token.colorTextLightSolid, border: 'none', fontWeight: 700, fontSize: 18 };
        if (key.type === 'op')       return { ...base, background: token.colorFillSecondary, color: token.colorPrimary, fontWeight: 700 };
        if (key.type === 'danger')   return { ...base, background: token.colorErrorBg, color: token.colorError };
        if (key.type === 'k')        return { ...base, background: token.colorInfoBg,  color: token.colorInfo, fontSize: 11, fontWeight: 700 };
        if (key.type === 'round')    return { ...base, background: token.colorWarningBg, color: token.colorWarning, fontSize: 13, fontWeight: 700 };
        return { ...base, background: token.colorBgContainer };
    };

    return (
        <Modal
            title={<span><CalculatorOutlined style={{ marginRight: 8 }} />Máy tính</span>}
            open={open}
            onCancel={onClose}
            footer={null}
            width={300}
            styles={{ body: { padding: '12px 16px 16px' } }}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {/* Expression input */}
                <Input
                    ref={inputRef}
                    value={expr}
                    onChange={handleInputChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); if (e.key === 'Escape') onClose(); }}
                    status={hasError ? 'error' : undefined}
                    style={{ fontFamily: 'monospace', fontSize: 14 }}
                    placeholder="Nhập hoặc bấm bàn phím bên dưới"
                />

                {/* Result display */}
                <div style={{
                    background: token.colorFillTertiary, borderRadius: token.borderRadius,
                    padding: '4px 12px', textAlign: 'right', minHeight: 38,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                }}>
                    {hasError
                        ? <span style={{ color: token.colorError, fontSize: 12 }}>Biểu thức không hợp lệ</span>
                        : result !== null
                            ? <span style={{ color: token.colorSuccess, fontSize: 22, fontWeight: 700 }}>= {formatNumber(result)}</span>
                            : <span style={{ color: token.colorTextDisabled, fontSize: 20 }}>—</span>
                    }
                </div>

                {/* Keypad */}
                <Space direction="vertical" style={{ width: '100%' }} size={5}>
                    {KEYPAD.map((row, ri) => (
                        <Flex key={ri} gap={5}>
                            {row.map((key, ci) => (
                                <div key={ci} style={cellStyle(key)} onClick={() => handleKey(key)}>
                                    {key.label}
                                </div>
                            ))}
                        </Flex>
                    ))}
                </Space>

                {/* Apply */}
                <Button
                    type="primary" block icon={<CheckOutlined />}
                    disabled={result === null} onClick={handleApply} size="large"
                >
                    Áp dụng{result !== null ? ` — ${formatNumber(result)}` : ''}
                </Button>
            </Space>
        </Modal>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const InputNumber2 = ({
    value,
    onChange,
    style,
    min,
    max,
    placeholder,
    disabled,
    readOnly,
    className,
    size,
    showCalculator: showCalcProp = true,
}) => {
    const { token } = theme.useToken();
    const [displayValue, setDisplayValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const inputRef = useRef();

    useEffect(() => {
        if (!isEditing) {
            setDisplayValue(value != null ? formatNumber(value) : '');
        }
    }, [value, isEditing]);

    const handleFocus = () => {
        setIsEditing(true);
        setDisplayValue(value != null ? String(value) : '');
        setPreview(null);
        setHasError(false);
    };

    const handleChange = (e) => {
        const input = e.target.value;
        if (!/^[-]?\d*(\.\d*)?$/.test(input)) {
            return; // Prevent non-numeric input
        }
        setDisplayValue(input);
        setHasError(false);
        if (input.trim() === '') { setPreview(null); return; }
        if (isExpression(input)) {
            const result = evaluateExpression(input);
            if (result !== null) { setPreview(result); }
            else { setPreview(null); setHasError(true); }
        } else {
            setPreview(null);
        }
    };

    const commit = useCallback(() => {
        setIsEditing(false);
        setPreview(null);
        setHasError(false);
        const str = displayValue.trim();
        if (str === '') {
            setDisplayValue('');
            if (onChange) onChange(null);
            return;
        }
        let result;
        if (isExpression(str)) {
            result = evaluateExpression(str);
            if (result === null) {
                setDisplayValue(value != null ? formatNumber(value) : '');
                return;
            }
        } else {
            result = parseRawNumber(str);
            if (result === null) {
                setDisplayValue(value != null ? formatNumber(value) : '');
                return;
            }
        }
        if (min != null) result = Math.max(min, result);
        if (max != null) result = Math.min(max, result);
        result = Math.round(result * 1e10) / 1e10;
        setDisplayValue(formatNumber(result));
        if (onChange) onChange(result);
    }, [displayValue, value, onChange, min, max]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { commit(); inputRef.current?.blur(); }
        else if (e.key === 'Escape') {
            setIsEditing(false);
            setDisplayValue(value != null ? formatNumber(value) : '');
            setPreview(null);
            setHasError(false);
            inputRef.current?.blur();
        }
    };

    const handleApplyFromModal = (result) => {
        if (min != null) result = Math.max(min, result);
        if (max != null) result = Math.min(max, result);
        result = Math.round(result * 1e10) / 1e10;
        setDisplayValue(formatNumber(result));
        if (onChange) onChange(result);
    };

    const suffix = (() => {
        if (isEditing && preview !== null)
            return (
                <span style={{ color: token.colorSuccess, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    = {formatNumber(preview)}
                </span>
            );
        if (isEditing && hasError)
            return <span style={{ color: token.colorError, fontSize: 13, fontWeight: 700 }}>!</span>;
        return null;
    })();

    const tooltipTitle = (() => {
        if (!isEditing) return null;
        if (preview !== null)
            return (
                <span>
                    Kết quả: <strong>{formatNumber(preview)}</strong> — nhấn <kbd style={{ background: 'rgba(255,255,255,.2)', padding: '0 4px', borderRadius: 3 }}>Enter</kbd> để xác nhận
                </span>
            );
        if (hasError) return 'Biểu thức không hợp lệ';
        return null;
    })();

    return (
        <>
            <Space.Compact>
                <Tooltip
                    open={isEditing && (preview !== null || hasError)}
                    title={tooltipTitle}
                    color={hasError ? token.colorError : token.colorPrimary}
                    placement="bottomLeft"
                >
                    <Input
                        ref={inputRef}
                        value={displayValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={commit}
                        onKeyDown={handleKeyDown}
                        suffix={suffix}
                        status={hasError ? 'error' : undefined}
                        style={style ?? { minWidth: 120 }}
                        placeholder={placeholder ?? 'Nhập số hoặc phép tính'}
                        disabled={disabled}
                        readOnly={readOnly}
                        className={className}
                        size={size}
                    />
                </Tooltip>
                {showCalcProp && (
                    <Button
                        icon={<CalculatorOutlined />}
                        onMouseDown={(e) => { e.preventDefault(); setModalOpen(true); }}
                        disabled={disabled}
                        size={size}
                    />
                )}
            </Space.Compact>

                {showCalcProp && (
                    <CalculatorModal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        onApply={handleApplyFromModal}
                        token={token}
                        initialValue={value} // Pass the current value to the calculator modal
                    />
                )}
        </>
    );
};

export default InputNumber2;